import React from "react";

import { Box } from "@material-ui/core";

import OutputAreasMap, { pointerInfoEmpty } from "components/visuals/maps/OutputAreasMap";
import useColourPalette from "components/visuals/useColourPalette";
import Explore from "pages/customer/tools/location/map/Explore";
import PinnedLocation from "pages/customer/tools/location/map/PinnedLocation";

import Legend from "./Legend";

import { selectOutputAreaFootfall } from "modules/customer/tools/location/footfall/footfallSlice";
import { selectPinnedLocation } from "modules/customer/tools/location/locationSlice";
import { selectMapboxConfiguration } from "modules/siteConfiguration/siteConfigurationSlice";
import { useAppSelector } from "store";

enum ColourGroup {
    VeryLow,
    Low,
    Medium,
    High,
    VeryHigh
}

const Map: React.FC = () => {
    const colourPalette = useColourPalette();
    const colours = [colourPalette.sequential[4], colourPalette.sequential[3], colourPalette.sequential[2], colourPalette.sequential[1], colourPalette.sequential[0]];
    const mapboxConfiguration = useAppSelector(selectMapboxConfiguration);
    const outputAreas = useAppSelector(selectOutputAreaFootfall);
    const retailCentre = useAppSelector(selectPinnedLocation)?.retailCentre;
    const [hoverInfo, setHoverInfo] = React.useState(pointerInfoEmpty);
    const hoveredOutputArea = outputAreas.find(outputArea => outputArea.outputAreaCode === hoverInfo.outputAreaCode);
    const [initialViewport, setInitialViewport] = React.useState({
        latitude: retailCentre?.latitude ?? 0,
        longitude: retailCentre?.longitude ?? 0,
        zoom: 13.5,
        minZoom: 4
    });

    const mapOutputAreas = React.useMemo(() => {
        return outputAreas.map(outputArea => ({
            code: outputArea.outputAreaCode,
            colourGroup: outputArea.footfallDensity >= 151
                ? ColourGroup.VeryHigh
                : outputArea.footfallDensity >= 61
                    ? ColourGroup.High
                    : outputArea.footfallDensity >= 31
                        ? ColourGroup.Medium
                        : outputArea.footfallDensity >= 11 ? ColourGroup.Low : ColourGroup.VeryLow
        }));
    }, [outputAreas]);

    React.useEffect(() => {
        setInitialViewport(current => {
            return {
                ...current,
                latitude: retailCentre?.latitude ?? 0,
                longitude: retailCentre?.longitude ?? 0
            };
        });
    }, [retailCentre]);

    return (
        <Box data-cy="footfall-map">
            <OutputAreasMap
                loading={false}
                error={false}
                mapboxAccessToken={mapboxConfiguration.accessToken}
                mapboxBaseMapStyle={mapboxConfiguration.baseMapStyle}
                mapboxOutputAreasTilesetId={mapboxConfiguration.outputAreasTilesetId}
                mapboxOutputAreasTilesetUrl={mapboxConfiguration.outputAreasTilesetUrl}
                height="100vh"
                initialViewport={initialViewport}
                addGeocoder={false}
                outputAreas={mapOutputAreas}
                colours={colours}
                enableHover={true}
                onHover={setHoverInfo}
                enableClick={false}
                displayPOIs={true}
                downloadData={outputAreas}
            >
                <Explore />
                <PinnedLocation />
                <Legend outputArea={hoveredOutputArea} />
            </OutputAreasMap>
        </Box>
    );
};

export default Map;
